import React from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Trans, useLingui } from "@lingui/react/macro";

import SETTINGS from "../../lib/TallyLibrary/constants/configGeneral.json";
import axios from "../../utils/axios";
import { createPartnerCompany } from "./partnerApi";

interface CreatePartnerCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => Promise<void> | void;
}

const CreatePartnerCompanyDialog: React.FC<CreatePartnerCompanyDialogProps> = ({
  open,
  onClose,
  onCreated,
}) => {
  const { t } = useLingui();
  const theme = useTheme();
  const isDesktopScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [newCompanyName, setNewCompanyName] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);

  const [termsAccepted, setTermsAccepted] = React.useState(false);
  const [dppAccepted, setDppAccepted] = React.useState(false);
  const [termsHash, setTermsHash] = React.useState<string | null>(null);
  const [dppHash, setDppHash] = React.useState<string | null>(null);
  const [termsUrl, setTermsUrl] = React.useState<string | null>(null);
  const [dppUrl, setDppUrl] = React.useState<string | null>(null);
  const [agreementsLoading, setAgreementsLoading] = React.useState(false);
  const [agreementsError, setAgreementsError] = React.useState<string | null>(
    null,
  );

  const resetDialogState = React.useCallback(() => {
    setNewCompanyName("");
    setCreateError(null);
    setTermsAccepted(false);
    setDppAccepted(false);
    setTermsHash(null);
    setDppHash(null);
    setTermsUrl(null);
    setDppUrl(null);
    setAgreementsError(null);
    setAgreementsLoading(false);
  }, []);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const fetchAgreements = async () => {
      setAgreementsLoading(true);
      setAgreementsError(null);
      try {
        const [termsRes, dppRes] = await Promise.all([
          axios.get("/v1/anonymous/terms_and_conditions/"),
          axios.get("/v1/anonymous/data_protection_policy/"),
        ]);
        setTermsHash(termsRes.data.agreement_hash);
        setTermsUrl(termsRes.data.content);
        setDppHash(dppRes.data.agreement_hash);
        setDppUrl(dppRes.data.content);
      } catch {
        setAgreementsError(
          t`Failed to load agreement documents. Please try again.`,
        );
      } finally {
        setAgreementsLoading(false);
      }
    };

    fetchAgreements();
  }, [open, t]);

  const handleClose = () => {
    onClose();
    resetDialogState();
  };

  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) return;
    setCreating(true);
    setCreateError(null);
    try {
      const response = await createPartnerCompany(
        newCompanyName.trim(),
        termsHash,
        dppHash,
      );
      if (!response.data) {
        setCreateError(t`Failed to create company.`);
        return;
      }
      await onCreated();
      handleClose();
    } catch (error: any) {
      if (error?.response?.status === 403) {
        setCreateError(t`You do not have permission to create companies.`);
      } else {
        setCreateError(t`Failed to create company.`);
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: theme.spacing(2),
          marginLeft: isDesktopScreen
            ? `calc(${SETTINGS.drawerWidth}px)`
            : "auto",
          paddingBottom: theme.spacing(2.5),
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            color: theme.palette.primary.main,
            paddingLeft: 0,
            paddingRight: 0,
            paddingTop: 0,
          }}
        >
          <Trans context="Partner portal button and dialog title for creating a company">
            Create Company
          </Trans>
        </Box>
      </DialogTitle>
      <DialogContent>
        {createError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {createError}
          </Alert>
        )}
        {agreementsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {agreementsError}
          </Alert>
        )}
        <TextField
          autoFocus
          margin="dense"
          variant="filled"
          label={
            <Trans context="Partner portal company name column and form label">
              Company Name
            </Trans>
          }
          fullWidth
          value={newCompanyName}
          onChange={(e) => setNewCompanyName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleCreateCompany();
          }}
          InputProps={{
            style: {
              borderRadius: theme.spacing(2),
              paddingBottom: theme.spacing(1),
            },
            disableUnderline: true,
          }}
        />
        {agreementsLoading ? (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box mt={1}>
            <FormControlLabel
              sx={{
                alignItems: "flex-start",
                marginRight: 0,
                "& .MuiFormControlLabel-label": {
                  color: theme.palette.text.primary,
                },
              }}
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  sx={{ paddingTop: "2px" }}
                />
              }
              label={
                <Trans context="Partner portal terms and conditions checkbox label">
                  I agree to the{" "}
                  <a
                    href={termsUrl ?? "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms and Conditions
                  </a>
                </Trans>
              }
            />
            <FormControlLabel
              sx={{
                alignItems: "flex-start",
                marginRight: 0,
                "& .MuiFormControlLabel-label": {
                  color: theme.palette.text.primary,
                },
              }}
              control={
                <Checkbox
                  checked={dppAccepted}
                  onChange={(e) => setDppAccepted(e.target.checked)}
                  sx={{ paddingTop: "2px" }}
                />
              }
              label={
                <Trans context="Partner portal data protection policy checkbox label">
                  I agree to the{" "}
                  <a
                    href={dppUrl ?? "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Data Protection Policy
                  </a>
                </Trans>
              }
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          marginRight: theme.spacing(2),
        }}
      >
        <Button
          onClick={handleClose}
          disabled={creating}
          variant="outlined"
          sx={{
            borderRadius: theme.spacing(2),
          }}
        >
          <Trans context="Cancel button in partner create company dialog">
            Cancel
          </Trans>
        </Button>
        <Button
          onClick={handleCreateCompany}
          disabled={
            creating ||
            !newCompanyName.trim() ||
            !termsAccepted ||
            !dppAccepted ||
            termsHash === null ||
            dppHash === null
          }
          variant="contained"
          sx={{
            borderRadius: theme.spacing(2),
          }}
        >
          {creating ? (
            <CircularProgress size={20} />
          ) : (
            <Trans context="Submit button in partner create company dialog">
              Create
            </Trans>
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePartnerCompanyDialog;
